/**
 * Version History Component
 *
 * Display and manage configuration versions with activate/delete functionality
 */

import { useConfiguration } from '../../../contexts/ConfigurationContext';
import InstructionPanel from '../../../components/ui/InstructionPanel/InstructionPanel';
import { Button } from '../../../components/ui/Button2';
import { ConfigListSkeleton } from './ConfigurationSkeleton';
import { Badge } from '../../../components/ui/Badge';
import StatusPill from '../../../components/ui/StatusPill/StatusPill';
import { useConfirm } from '../../../components/ui/AlertDialog';
import { Icon } from '../../../components/ui/Icon';
import styles from './VersionHistory.module.css';

export default function VersionHistory() {
  const {
    versions,
    loading,
    activateVersion,
    deleteVersion
  } = useConfiguration();
  const { confirm, confirmDialog } = useConfirm();

  const handleActivate = async (version) => {
    if (await confirm({
      title: 'Activate version?',
      description: `Make version ${version} the current active configuration?`,
      confirmLabel: 'Activate',
    })) {
      await activateVersion(version);
    }
  };

  const handleDelete = async (version) => {
    if (await confirm({
      title: 'Delete version?',
      description: `Delete version ${version}? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    })) {
      await deleteVersion(version);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading && versions.length === 0) {
    return <ConfigListSkeleton />;
  }

  if (versions.length === 0) {
    return (
      <div className={styles.noData}>
        <h3>No Version History</h3>
        <p>No configuration versions found</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Configuration Version History</h2>
      </div>

      <div className={styles.versionsContainer}>
        {versions.map((version) => {
          const isActive = version.active;

          return (
            <div
              key={version.version}
              className={`${styles.versionCard} ${isActive ? styles.active : ''}`}
            >
              <div className={styles.versionHeader}>
                <div className={styles.versionInfo}>
                  <div className={styles.versionNumber}>
                    <span className={styles.versionLabel}>Version {version.version}</span>
                    {isActive && <StatusPill tone="success" label="Active" size="sm" />}
                  </div>
                  <div className={styles.versionMeta}>
                    <span className={styles.metaItem}>
                      <strong>Created:</strong> {formatDate(version.created_at)}
                    </span>
                    <span className={styles.metaItem}>
                      <strong>By:</strong> {version.created_by || 'Unknown'}
                    </span>
                  </div>
                </div>

                <div className={styles.versionActions}>
                  {!isActive && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleActivate(version.version)}
                        disabled={loading}
                      >
                        Activate
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(version.version)}
                        disabled={loading}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className={styles.versionBody}>
                <div className={styles.descriptionSection}>
                  <h4>Description</h4>
                  <p>{version.description || 'No description provided'}</p>
                </div>

                {version.notes && (
                  <div className={styles.notesSection}>
                    <h4>Notes</h4>
                    <p>{version.notes}</p>
                  </div>
                )}

                {version.tags && version.tags.length > 0 && (
                  <div className={styles.tagsSection}>
                    <h4>Tags</h4>
                    <div className={styles.tags}>
                      {version.tags.map((tag, index) => (
                        <Badge key={index} variant="soft" radius="full" color="blue">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Help text */}
      <InstructionPanel title="Version Management">
        <ul>
          <li><strong>Activate:</strong> Make a previous version the current active configuration</li>
          <li><strong>Delete:</strong> Permanently remove an inactive configuration version</li>
          <li>Only one version can be active at a time</li>
          <li>Active versions cannot be deleted</li>
          <li>All changes are logged in the audit trail</li>
        </ul>
      </InstructionPanel>

      {confirmDialog}
    </div>
  );
}
